<script setup lang="ts">
import { useRegisterLink } from "~/composables/useRegisterLink";
import { useAnalytics } from "~/composables/useAnalytics";
import { useCalendlyModal } from "~/composables/useCalendlyModal";

const registerLink = useRegisterLink();
const { trackSignupClick } = useAnalytics();
const { openCalendly } = useCalendlyModal();
</script>

<template>
  <section class="w-full py-20 lg:py-28">
    <div class="max-w-[1200px] mx-auto px-5 sm:px-8">
      <div class="cta-carte">
        <div class="cta-halo" aria-hidden="true" />

        <h2 class="cta-titre">
          <span class="cta-titre-1">{{ $t("accueil.ctaFinal.titre1") }}</span>
          <span class="cta-titre-2">{{ $t("accueil.ctaFinal.titre2") }}</span>
        </h2>

        <p class="cta-accroche">{{ $t("accueil.ctaFinal.accroche") }}</p>

        <div class="cta-actions">
          <NuxtLink
            v-register-cta="'bandeau-final'"
            :to="registerLink"
            class="cta-btn cta-btn--plein"
            @click="trackSignupClick('cta_banner')"
          >
            {{ $t("navigation.appHeader.inscrireAssociation") }}
          </NuxtLink>
          <button type="button" class="cta-btn cta-btn--contour" @click="openCalendly">
            {{ $t("accueil.ctaFinal.planifierAppel") }}
          </button>
        </div>

        <p class="cta-rassurance">{{ $t("accueil.ctaFinal.rassurance") }}</p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.cta-carte {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4.5rem 2rem 4rem;
  border-radius: 2rem;
  background: #050816;
}

/* Halo bleu au centre : la lumière part du titre et se perd dans le noir. */
.cta-halo {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: radial-gradient(
    ellipse 45% 60% at 50% 48%,
    rgba(47, 116, 255, 0.42) 0%,
    rgba(12, 48, 151, 0.22) 45%,
    transparent 78%
  );
}

.cta-titre {
  display: flex;
  flex-direction: column;
  gap: 0.15em;
  margin-bottom: 1.25rem;
  font-size: clamp(2.2rem, 5.2vw, 4.4rem) !important;
  line-height: 0.98 !important;
}

.cta-titre-1 {
  color: #fff;
}

.cta-titre-2 {
  color: #6aa4ff;
}

.cta-accroche {
  max-width: 34rem;
  margin-bottom: 2rem;
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  line-height: 1.6;
  color: rgba(226, 232, 255, 0.72);
}

.cta-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.cta-btn:hover {
  transform: translateY(-1px);
}

.cta-btn--plein {
  background: #1f6fff;
  color: #fff;
}

.cta-btn--plein:hover {
  background: #3a82ff;
  box-shadow: 0 10px 30px rgba(31, 111, 255, 0.35);
}

.cta-btn--contour {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.24);
}

.cta-btn--contour:hover {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.06);
}

.cta-rassurance {
  margin-top: 1.25rem;
  font-size: 0.875rem;
  color: rgba(226, 232, 255, 0.5);
}

@media (max-width: 639px) {
  .cta-carte {
    padding: 3rem 1.25rem 2.75rem;
    border-radius: 1.5rem;
  }

  .cta-actions {
    flex-direction: column;
    align-self: stretch;
  }
}
</style>
